import React, { useContext, useState } from "react";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    DialogActions,
    Button,
    Input,
    Checkbox,
    RadioGroup,
    Radio,
    Field,
    Divider,
} from "@fluentui/react-components";
import CreatableCompanySearch from "./CreatableCompanySearch";
import CreatableProjectSearch from "../components/CreatableProjectSearch";
import { IDropdownElement } from "./WeekTable";
import { IProject, IUser } from "../types/types";
import { ApiContext, ICompany } from "../providers/ApiProvider";
import { MAX_PROJECT_NAME_LENGTH } from "../utils/constants";

interface IAddProjectDialogTypes {
    dropdownArr: IDropdownElement[];
    token: string;
    user: IUser;
    passChildData: (data: IDropdownElement) => void;
    handleSelectedProject: (project: IDropdownElement) => void;
    initialOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
}
const ADD_NEW_COMPANY = "Add new company";

export default function AddProjectDialog(props: IAddProjectDialogTypes) {
    const { createProject } = useContext(ApiContext);
    const [projects, setProjects] = useState<IDropdownElement[]>([]);
    const [companies, setCompanies] = useState<string[]>([ADD_NEW_COMPANY]);
    const [selectedCompany, setSelectedCompany] = useState<string>();
    const [selectedProject, setSelectedProject] = useState<string | number>();
    const [popup, setPopup] = useState<boolean>(props.initialOpen || false);
    
    // Sync external control
    React.useEffect(() => {
        if (props.initialOpen !== undefined) {
            setPopup(props.initialOpen);
        }
    }, [props.initialOpen]);
    
    const handlePopupChange = (newValue: boolean) => {
        setPopup(newValue);
        if (props.onOpenChange) {
            props.onOpenChange(newValue);
        }
    };
    const [isPrivate, setIsPrivate] = useState<boolean>(true);
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [startDate, setStartDate] = useState<number | null>(null);
    const [endDate, setEndDate] = useState<number | null>(null);
    const [locked, setLocked] = useState<boolean | undefined | "mixed">(false);
    const [description, setDescription] = useState<string>("");

    const clearFields = () => {
        console.log("Clearing fields");
        setProjects([]);
        setCompanies([ADD_NEW_COMPANY]);
        setSelectedCompany("");
        setSelectedProject("");
        setPopup(false);
        setIsPrivate(true);
        setIsDisabled(true);
        setStartDate(null);
        setEndDate(null);
        setLocked(false);
        setDescription("");
    };

    const createNewProject = async (name: string, companyID: string) => {
        const body: IProject = {
            company_id: companyID,
            owner_oid: "",
            project_name: name,
            members: isPrivate ? [props.user.oid] : ["offcodeGroup"],
            admins: [],
            entry_locked: locked ? true : false,
            tags: ["tag1"],
            private: isPrivate,
            active: {
                from: startDate,
                to: endDate,
            },
            budget: {
                used: null,
                total: null,
            },
            note: description || null,
        };
        const ret = await createProject(props.token, body);
        const newDropdownElement: IDropdownElement = {
            label: companyID,
            project_id: ret.id!,
            value: name,
            private: isPrivate,
            key: companyID + name,
            image: null,
        };
        setProjects([...projects, newDropdownElement]);
        props.passChildData(newDropdownElement);
        //clear all fields
        clearFields();
        return ret;
    };

    const handleCompanies = (company: ICompany) => {
        setSelectedCompany(company.id);
        setCompanies([...companies, company.company_name]);
        setIsDisabled(false);
    };

    const handleDisabled = (disabled: boolean | ((prevState: boolean) => boolean)) => {
        setIsDisabled(disabled);
    };

    return (
        <>
            {/* Desktop/table view: show searchable project dropdown; opening this should NOT auto-open the dialog */}
            {props.initialOpen === undefined && (
                <div>
                    <CreatableProjectSearch
                        projectList={props.dropdownArr}
                        styles={{}}
                        selectedProject={props.handleSelectedProject}
                        passSelectedProject={setSelectedProject}
                        setPopUp={setPopup}
                    />
                </div>
            )}
            <Dialog open={popup} onOpenChange={(_, data) => handlePopupChange(data.open)}>
            <DialogSurface className="flex flex-col w-full max-w-[95vw] sm:max-w-[600px] max-h-[90vh] sm:max-h-[800px] overflow-hidden">
                <DialogBody className="p-4 sm:p-6 flex-1 flex flex-col overflow-auto">
                    <DialogTitle className="text-lg sm:text-xl">Add New Project</DialogTitle>
                    <DialogContent className="px-0">
                        <form
                            onSubmit={async (e) => {
                                e.preventDefault();
                                const formData = new FormData(e.target as HTMLFormElement);
                                const projectName = formData.get("projectName") as string;
                                if (selectedCompany) {
                                    try {
                                        const project = await createNewProject(projectName, selectedCompany);
                                        const dropdownElement: IDropdownElement = {
                                            label: selectedCompany,
                                            project_id: project.id!,
                                            value: projectName,
                                            private: isPrivate,
                                            key: selectedCompany + projectName,
                                            image: null,
                                        };
                                        props.handleSelectedProject(dropdownElement as IDropdownElement);
                                        handlePopupChange(false);
                                    } catch (error) {
                                        console.error(error);
                                    }
                                }
                            }}
                            className="flex flex-col gap-4 sm:gap-5 w-full"
                        >
                            <CreatableCompanySearch
                                selectedCompany={handleCompanies}
                                styles={{}}
                                handleDisabled={handleDisabled}
                                user={props.user}
                            />

                            <Field label="Project Name" required>
                                <Input
                                    name="projectName"
                                    maxLength={MAX_PROJECT_NAME_LENGTH}
                                    minLength={1}
                                    disabled={isDisabled}
                                    value={selectedProject?.toString() || ""}
                                    onChange={(e) => {
                                        setSelectedProject(e.target.value);
                                    }}
                                />
                            </Field>

                            <Divider />

                            <Field label="Description / Notes">
                                <textarea
                                    disabled={
                                        isDisabled ||
                                        (!isPrivate && !props.user.is_admin && !props.user.is_project_manager)
                                    }
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Enter project description or notes"
                                    className="w-full min-h-[80px] rounded border border-(--colorNeutralStroke1) bg-(--colorNeutralBackground1) px-3 py-2 text-(--colorNeutralForeground1)"
                                />
                            </Field>

                            <Divider />

                            <Field label="Select active start date">
                                <Input
                                    type="date"
                                    disabled={isDisabled}
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            setStartDate(new Date(e.target.value).getTime());
                                        }
                                    }}
                                />
                            </Field>

                            <Field label="Select active end date">
                                <Input
                                    type="date"
                                    disabled={isDisabled}
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            setEndDate(new Date(e.target.value).getTime());
                                        }
                                    }}
                                />
                            </Field>

                            {props.user.is_admin ? (
                                <Field label="Project Type">
                                    <RadioGroup
                                        value={isPrivate ? "private" : "public"}
                                        onChange={(e, data) => {
                                            if (data.value === "public") {
                                                setIsPrivate(false);
                                            } else {
                                                setIsPrivate(true);
                                            }
                                        }}
                                    >
                                        <Radio value="private" label="Private" />
                                        <Radio value="public" label="Public" />
                                    </RadioGroup>
                                </Field>
                            ) : null}

                            <Divider />

                            <Field>
                                <Checkbox
                                    label="Entry locked"
                                    checked={locked === true}
                                    onChange={(e, data) => {
                                        setLocked(data.checked);
                                    }}
                                    disabled={isDisabled}
                                />
                            </Field>

                            <DialogActions className="flex-col sm:flex-row gap-2 sm:gap-0 pt-2">
                                <DialogTrigger disableButtonEnhancement>
                                    <Button 
                                        appearance="secondary" 
                                        onClick={() => handlePopupChange(false)}
                                        className="w-full sm:w-auto min-h-[44px] touch-manipulation"
                                    >
                                        Cancel
                                    </Button>
                                </DialogTrigger>
                                <Button 
                                    appearance="primary" 
                                    type="submit" 
                                    disabled={isDisabled}
                                    className="w-full sm:w-auto min-h-[44px] touch-manipulation"
                                >
                                    Submit
                                </Button>
                            </DialogActions>
                        </form>
                    </DialogContent>
                </DialogBody>
            </DialogSurface>
        </Dialog>
        </>
    );
}
